<template>
  <modal show-close-btn @leave="$emit('leave')">
    <loader v-if="loading" />
    <div v-else-if="editAddress">
      <h3 class="mb-6">{{ editAddress.id ? 'Update' : 'New' }} Delivery Address</h3>
      <span class="text-blue hover:underline mb-6 inline-block cursor-pointer" @click="editAddress = null">&lt; Cancel</span>

      <div class="flex flex-wrap -mx-2 mb-6" v-if="!editAddress.id">
        <div class="w-full md:w-1/4 px-2">
          <label for="lookup_postcode">Post Code:</label>
          <input type="text" v-model="editAddress.postcode" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3 uppercase" maxlength="8" />
        </div>

        <div class="w-full md:w-3/4 px-2">
          <label for="lookup_postcode">Address:</label>
          <loader v-if="searching" />
          <select v-else v-model="selectedSuggestion" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3">
            <option :value="null">[Please Select]</option>
            <option v-for="(suggestion, i) in suggestions" :key="i" :value="suggestion.data">
              {{ suggestion.formatted }}
            </option>
          </select>
        </div>
      </div>
      <p v-else class="mb-6 bg-grey-lighter p-3"><strong>Please note:</strong> Updating this delivery address will also update existing orders assigned to this address.</p>

      <div class="mb-6">
        <label>Recipient Name:</label>
        <input type="text" v-model="editAddress.recipient" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3" />
      </div>
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 px-2 mb-6">
          <label>Line 1:</label>
          <input type="text" v-model="editAddress.name_number" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3" />
        </div>
        <div class="w-full md:w-1/2 px-2 mb-6">
          <label>Line 2:</label>
          <input type="text" v-model="editAddress.line2" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3" />
        </div>
      </div>
      <div class="mb-6">
        <label>Line 3:</label>
        <input type="text" v-model="editAddress.line3" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3" />
      </div>
      <div class="mb-6">
        <label>Town:</label>
        <input type="text" v-model="editAddress.town" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3" />
      </div>
      <div class="mb-6">
        <label>County:</label>
        <input type="text" v-model="editAddress.line4" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3" />
      </div>
      <div class="mb-6" v-if="editAddress.id">
        <label>Post Code:</label>
        <input type="text" v-model="editAddress.postcode" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3 uppercase" maxlength="8" />
      </div>

      <div class="flex justify-end">
        <button @click="save" class="bg-primary rounded py-3 px-12 text-white">
          <loader v-if="saving" fill-color="white" />
          <span v-else>Save Address</span>
        </button>
      </div>
    </div>
    <div v-else>
      <div class="w-full flex">
        <div class="w-2/3">
          <h3 class="mb-6">Select Delivery Address For: {{ name }}</h3>
        </div>
        <div class="w-1/3 text-right">
          <i class="fas fa-star text-yellow-dark pr-2"></i>
          <span>Default Delivery Address</span>
        </div>
      </div>
      <div class="w-full rounded bg-grey-lightest flex overflow-hidden mb-3 border" :class="deliveryAddress.id === content ? 'border-green-dark' : ''" v-for="deliveryAddress in deliveryAddresses" :key="deliveryAddress.id">
        <div class="w-10 p-3 flex items-center justify-center" :class="deliveryAddress.id === content ? 'bg-green-dark text-white' : 'bg-grey-lighter'">
          <i class="fas fa-check" v-if="deliveryAddress.id === content"></i>
        </div>
        <div class="flex-grow p-3" :class="deliveryAddress.id === content ? 'bg-green-lightest' : 'hover:bg-grey-light cursor-pointer'" @click="content = deliveryAddress.id">
          {{ deliveryAddress.address }}
        </div>
        <div class="w-10 bg-grey-lighter hover:bg-primary hover:text-white p-3 flex items-center justify-center cursor-pointer" title="Choose Default Address" @click="setPreferredAddress(deliveryAddress.id)">
          <i v-if="settingPreferredAddress === deliveryAddress.id" class="fas fa-circle-notch fa-spin"></i>
          <i v-else class="fa-star" :class="deliveryAddress.preferred ? 'fas text-yellow-dark' : 'far'"></i>
        </div>
        <div v-if="deliveryAddress.id !== null" class="w-10 bg-grey-lighter hover:bg-primary hover:text-white p-3 flex items-center justify-center cursor-pointer" title="Edit Address" @click="getAddress(deliveryAddress.id)">
          <i class="fas fa-pencil-alt"></i>
        </div>
        <div v-if="deliveryAddress.id !== null" class="w-10 bg-grey-lighter hover:bg-red-dark hover:text-white p-3 flex items-center justify-center cursor-pointer" title="Remove Address" @click="deleteAddress(deliveryAddress)">
          <i class="fas fa-trash-alt"></i>
        </div>
      </div>

      <div class="text-center">
        <span class="text-blue hover:underline cursor-pointer" @click="startNewAddress">[+] Add New Delivery Address</span>
      </div>
    </div>
  </modal>
</template>

<script>
import api from '@/http'
import { mapActions, mapState } from 'vuex'
import Loader from './Loader.vue'
import debounce from 'lodash/debounce'

export default {
  components: { Loader },
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: Number,
      required: true
    },
    deliveryAddresses: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      content: this.value,
      editAddress: null,
      loading: false,
      saving: false,
      searching: false,
      suggestions: [],
      selectedSuggestion: null,
      settingPreferredAddress: false
    }
  },
  computed: {
    ...mapState(['selectedOffice'])
  },
  watch: {
    content () {
      this.$emit('input', this.content)
      this.$emit('change', this.content)
    },
    value () {
      this.content = this.value
    },
    selectedSuggestion (suggestion) {
      if (!suggestion) {
        return
      }

      this.editAddress.name_number = suggestion.address_name_number
      this.editAddress.line2 = suggestion.address_line2
      this.editAddress.line3 = suggestion.address_line3
      this.editAddress.town = suggestion.address_town
      this.editAddress.line4 = suggestion.address_line4
      this.editAddress.postcode = suggestion.address_postcode
    },
    'editAddress.postcode': {
      handler () {
        if (!this.editAddress.id) {
          this.search()
        }
      }
    }
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    getAddress (id) {
      this.loading = true

      api.get(`delivery-addresses/${id}`).then((response) => {
        this.editAddress = response.data
        this.loading = false
      }, (error) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: error.response.data.message
        })
        this.editAddress = null
        this.loading = false
      })
    },
    back () {
      if (this.saving || this.loading) {
        return
      }

      this.editAddress = null
    },
    startNewAddress () {
      this.selectedSuggestion = null
      this.suggestions = []
      this.editAddress = {
        office_id: this.selectedOffice.id,
        recipient: '',
        name_number: '',
        line2: '',
        line3: '',
        town: '',
        line4: '',
        postcode: ''
      }
    },
    save () {
      if (this.saving) {
        return
      }

      this.saving = true

      if (this.editAddress.id) {
        this.updateAddress()
        return
      }

      this.createAddress()
    },
    createAddress () {
      api.post(`delivery-addresses`, this.editAddress).then((response) => {
        this.deliveryAddresses.push(response.data)
        this.content = response.data.id
        this.saving = false
        this.editAddress = null
      }, (error) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: error.response.data.message
        })
        this.saving = false
      })
    },
    updateAddress () {
      api.patch(`delivery-addresses/${this.editAddress.id}`, this.editAddress).then((response) => {
        this.saving = false
        this.editAddress = null
        this.$emit('updateAddress', response.data)
      }, (error) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: error.response.data.message
        })
        this.saving = false
      })
    },
    setPreferredAddress (id) {
      this.settingPreferredAddress = id
      api.post(`delivery-addresses/set-preferred-address`, {
        office_id: this.selectedOffice.id,
        address_id: id
      }).then((response) => {
        this.deliveryAddresses.forEach(function (address) {
          if (address.id === response.data.id) {
            address.preferred = true
          } else {
            address.preferred = false
          }
        })
        this.settingPreferredAddress = false
      }, (error) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: error.response.data.message
        })
        this.settingPreferredAddress = false
      })
    },
    deleteAddress (address) {
      if (!confirm('Are you sure you want to remove this address?')) {
        return
      }

      api.delete(`delivery-addresses/${address.id}`).then(() => {
        if (this.content === address.id) {
          this.content = null
        }
        const index = this.deliveryAddresses.indexOf(address)
        this.deliveryAddresses.splice(index, 1)
      }, (error) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: error.response.data.message
        })
      })
    },
    search: debounce(function () {
      if (!RegExp('^[A-PR-UWYZ][A-HJ-Y]?[0-9][0-9A-HJKMNPR-Y]? ?[0-9][ABD-HJLNP-UW-Z]{2}$', 'gi').test(this.editAddress.postcode)) {
        return
      }

      this.selectedSuggestion = null
      this.searching = true
      api.get('/address-lookup', {
        params: {
          postcode: this.editAddress.postcode
        }
      }).then((response) => {
        this.suggestions = response.data
        this.searching = false
      }, () => {
        this.searching = false
      })
    }, 500)
  }
}
</script>
